import { conversions, Measurements } from '~/utility/BinSentry-ui-utility';
import { css, html, LitEntity, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import '../../common/bn-loading';
import '../../common/bn-placeholder';
import '../../common/bn-property';
import '../../../parcels/manufacturer-capacity-dialog';

class BinDetails extends LitEntity {
  static get properties() {
    return {
      capacity: { type: Number },
      horizontal: { type: Boolean, default: false, attribute: 'horizontal' },
    };
  }

  static get styles() {
    return [sharedStyles, css`
      .property-group {
        flex: 1;
      }
      :host([horizontal]) {
        gap: 3rem !important;
      }
      :host([horizontal]) .property-group {
        max-width: 25rem;
      }
      :host([horizontal]) .property-group + .property-group {
        margin-top: 0;
      }
      bn-property {
        --align-value: right;
      }
    `];
  }

  render() {
    if (this.loading) {
      return html`<bn-loading></bn-loading>`;
    }

    if (!this.entity) {
      return html`<bn-placeholder></bn-placeholder>`;
    }

    const { isAggregate } = this.entity.properties;

    return isAggregate ? this._renderAggregate() : this._renderBin();
  }

  _renderManufacturerCapacity() {
    const { listedCapacity } = this.entity.properties;

    let capacityDisplay = html`unknown`;
    if (typeof listedCapacity === 'number') {
      const convertedCapacity = conversions.kilogramsToOrderAmountPref(listedCapacity, Measurements.get('orderAmount').unit);
      capacityDisplay = convertedCapacity && html`${convertedCapacity} <span class="unit">${Measurements.get('orderAmount').unit}</span>`;
    }

    return html`
      <bn-property label="Manufacturer Capacity">
        ${capacityDisplay}
        ${renderIf(this.entity.hasAction('update-details'), () => html`
          <manufacturers-capacity-dialog .href="${this.href}"></manufacturers-capacity-dialog>
        `)}
      </bn-property>
    `;
  }

  _renderBin() {
    const { dimensions, volumes, role } = this.entity.properties;

    const profile = dimensions.profile === 1 ? 'Center Discharge' : dimensions.profile === 2 ? 'Side Discharge' : 'Unknown';
    const { top, body, hopper, ringCount, lipHeight, deviceDepth, totalHeight } = dimensions;

    // we only show calculated capacity if it has actually been calculated from
    // bin dimensions.  In the case of unmonitored bins it's inputted directly.
    const hasDimensions = ![body.radius, totalHeight].some(d => d === 0);
    const isIngredientBin = role === 'ingredient';

    return html`
      <div class="property-group">
        <h4>Details</h4>
        ${renderIf(!isIngredientBin, html`
          <bn-property label="Owner">
            <bin-owner .href="${this.href}"></bin-owner>
          </bn-property>
        `)}

        ${this._renderManufacturerCapacity()}

        ${renderIf(hasDimensions, () => html`
          <bn-property label="Calculated Capacity (Volume)">
            ${Measurements.get('volume').html({ volume: volumes.fullTotal })}
          </bn-property>
          <bn-property label="Calculated Capacity">
            ${Measurements.get('weight').html({ weight: this.capacity })}
          </bn-property>
        `)}
      </div>

      ${renderIf(!isIngredientBin, () => html`
        <div class="property-group">
          <h4>Dimensions</h4>
          <bn-property label="Profile">${profile}</bn-property>
          <bn-property label="Total Height"><bn-dimension value="${totalHeight}"></bn-dimension></bn-property>
          <bn-property label="Ring Count">${ringCount}</bn-property>

          <bn-property label="Lip Height"><bn-dimension value="${lipHeight}"></bn-dimension></bn-property>
          <bn-property label="Device Depth"><bn-dimension value="${deviceDepth}"></bn-dimension></bn-property>

          <bn-property label="Top Angle">${top.angle} <span class="unit">degrees</span></bn-property>
          <bn-property label="Top Height"><bn-dimension value="${top.height}"></bn-dimension></bn-property>
          <bn-property label="Top Diameter"><bn-dimension value="${top.truncatedRadius * 2}"></bn-dimension></bn-property>

          <bn-property label="Body Height"><bn-dimension value="${body.height}"></bn-dimension></bn-property>
          <bn-property label="Body Diameter"><bn-dimension value="${body.radius * 2}"></bn-dimension></bn-property>

          <bn-property label="Hopper Angle">${hopper.angle} <span class="unit">degrees</span></bn-property>
          <bn-property label="Hopper Height"><bn-dimension value="${hopper.height}"></bn-dimension></bn-property>
          <bn-property label="Hopper Diameter"><bn-dimension value="${hopper.truncatedRadius * 2}"></bn-dimension></bn-property>
        </div>
      `)}

      ${renderIf(isIngredientBin && dimensions.shape === 'circular', () => html`
        <div class="property-group">
          <h4>Dimensions</h4>
          <bn-property label="Radius"><bn-dimension value="${body.radius}"></bn-dimension></bn-property>
          <bn-property label="Total Height"><bn-dimension value="${totalHeight}"></bn-dimension></bn-property>
          <bn-property label="Top Height"><bn-dimension value="${top.height}"></bn-dimension></bn-property>
          <bn-property label="Body Height"><bn-dimension value="${body.height}"></bn-dimension></bn-property>
          <bn-property label="Hopper Height"><bn-dimension value="${hopper.height}"></bn-dimension></bn-property>
        </div>
      `)}

      ${renderIf(isIngredientBin && dimensions.shape === 'rectangular', () => html`
        <div class="property-group">
          <h4>Dimensions</h4>
          <bn-property label="Total Height"><bn-dimension value="${totalHeight}"></bn-dimension></bn-property>
          <bn-property label="Body Height"><bn-dimension value="${body.height}"></bn-dimension></bn-property>
          <bn-property label="Body Width"><bn-dimension value="${body.width}"></bn-dimension></bn-property>
          <bn-property label="Body Depth"><bn-dimension value="${body.depth}"></bn-dimension></bn-property>
          <bn-property label="Hopper Height"><bn-dimension value="${hopper.height}"></bn-dimension></bn-property>
        </div>
      `)}
    `;
  }

  _renderAggregate() {
    const {
      listedCapacity,
      volumes,
    } = this.entity.properties;

    return html`
      <div class="property-group">
        <bn-property label="Owner">
          <bin-owner .href="${this.href}"></bin-owner>
        </bn-property>
      </div>

      <div class="property-group">
        <bn-property label="Total Listed Capacity">
          ${Measurements.get('weight').html({ weight: listedCapacity })}
        </bn-property>
        <bn-property label="Total Volume">
          ${Measurements.get('volume').html({ volume: volumes.fullTotal })}
        </bn-property>
      </div>
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    if (!this.entity) {
      this.capacity = 0;
      return;
    }
    const { bulkDensity } = this.entity.properties;
    const { fullTotal } = this.entity.properties.volumes;
    this.capacity = Math.ceil(bulkDensity * fullTotal);
  }
}

window.customElements.define('bin-details', BinDetails);
